import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { LayoutData } from '@/types'

import {
    BadgeCheck,
    Hammer,
    Handshake,
    Hourglass,
    PencilRuler,
    Scissors,
    ShieldCheck,
    Trees
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Breadcrumbs } from '@/components/generals/breadcrumbs'
import { SectionHeading } from '@/components/generals/section-heading'
import { ShowroomSection } from '@/components/generals/showroom-section'
import { getLayoutData } from '@/lib/catalog'

const STATS = [
    { value: '۱۸', label: 'سال تجربه در ساخت مبلمان' },
    { value: '۴٬۲۰۰+', label: 'سفارش تحویل‌شده' },
    { value: '۳۲', label: 'استادکار و طراح' },
    { value: '۵ سال', label: 'ضمانت اسکلت و بدنه' }
]

const VALUES = [
    {
        icon: Hourglass,
        title: 'دوام پیش از مد',
        text: 'طرح‌هایی می‌سازیم که با گذر زمان کهنه نشوند و ساختاری که سال‌ها بدون لق‌شدن و گود افتادن دوام بیاورد.'
    },
    {
        icon: BadgeCheck,
        title: 'شفافیت در مواد',
        text: 'جنس هر بخش را دقیق می‌گوییم؛ از نوع چوب و تراکم فوم تا ترکیب پارچه. هیچ‌چیز زیر روکش پنهان نمی‌ماند.'
    },
    {
        icon: Handshake,
        title: 'همراهی پس از خرید',
        text: 'خرید از نارون پایان رابطهٔ ما نیست؛ تعمیر، تعویض روکش و مشاوره برای چیدمان همیشه در دسترس است.'
    }
]

const WORKSHOP_STEPS = [
    {
        icon: PencilRuler,
        title: 'طراحی و نقشه',
        text: 'هر طرح ابتدا روی کاغذ و سپس در اندازهٔ واقعی آزمایش می‌شود تا تناسب و راحتی آن دقیق باشد.'
    },
    {
        icon: Trees,
        title: 'انتخاب و خشک‌کردن چوب',
        text: 'چوب راش، گردو و بلوط را با دقت انتخاب می‌کنیم و در کوره تا رطوبت استاندارد خشک می‌کنیم.'
    },
    {
        icon: Hammer,
        title: 'ساخت و مونتاژ',
        text: 'اسکلت با اتصالات فاق و زبانه و دوبل چوبی ساخته می‌شود تا سال‌ها بدون صدا و لرزش کار کند.'
    },
    {
        icon: Scissors,
        title: 'رویه‌کوبی و پرداخت',
        text: 'فوم، پارچه یا چرم با دست روی بدنه کار می‌شود و سطوح چوبی با روغن طبیعی پرداخت می‌شوند.'
    },
    {
        icon: ShieldCheck,
        title: 'کنترل کیفیت',
        text: 'پیش از ارسال، هر قطعه از نظر استحکام، دوخت و پرداخت یک‌به‌یک بررسی می‌شود.'
    }
]

interface AboutPageProps {
    layout: LayoutData
}

const AboutPage: FC<AboutPageProps> = ({ layout }): ReactNode => {
    return (
        <Layout categories={layout.categories}>
            <Seo
                title='درباره نارون'
                description='داستان نارون؛ کارگاه مبلمان دست‌سازی که از سال ۱۳۸۷ با چوب خشک‌شده، اتصالات دست‌ساز و پارچه‌های مقاوم، مبلمانی برای سال‌ها زندگی می‌سازد.'
                ogImage={layout.shareImage}
            />

            <section aria-labelledby='about-title' className='pb-16 pt-8 lg:pb-20 lg:pt-12'>
                <Container>
                    <Breadcrumbs
                        items={[
                            { key: 'home', label: 'خانه', href: '/' },
                            { key: 'about', label: 'درباره نارون' }
                        ]}
                    />

                    <div className='mt-8 grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-14'>
                        <SectionHeading
                            as='h1'
                            eyebrow='درباره نارون'
                            title='از یک کارگاه کوچک، با عشق به چوب'
                            titleId='about-title'
                            className='lg:col-span-6'
                        />

                        <p className='text-base leading-9 text-muted-foreground sm:text-lg lg:col-span-6'>
                            نارون در سال ۱۳۸۷ در کارگاهی کوچک در جنوب تهران شروع شد؛ با دو نجار، یک میز کار و این باور که مبلمان خوب باید نسل‌به‌نسل بماند. امروز تیم ما بزرگ‌تر شده، اما هنوز هر قطعه را همان‌طور می‌سازیم: آرام، دقیق و با دست.
                        </p>
                    </div>

                    <div className='mt-12 aspect-[4/3] overflow-hidden rounded-[2rem] bg-secondary sm:aspect-[16/9] lg:aspect-[21/9]'>
                        <img
                            src='/images/workshop.jpg'
                            alt='میز کار نجاری کارگاه نارون با ابزارهای دستی'
                            fetchPriority='high'
                            className='size-full object-cover'
                        />
                    </div>
                </Container>
            </section>

            <section aria-labelledby='name-title' className='py-16 lg:py-24'>
                <Container className='grid gap-14 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-6'>
                        <SectionHeading
                            eyebrow='داستان یک نام'
                            title='چرا نارون؟'
                            titleId='name-title'
                        />

                        <div className='mt-6 space-y-5 text-base leading-9 text-muted-foreground'>
                            <p>
                                نارون نام درختی مقاوم و سایه‌گستر است که روزگاری در کوچه‌باغ‌های ایران فراوان دیده می‌شد. این نام را انتخاب کردیم چون می‌خواهیم مبلمان‌مان هم مثل همین درخت، سال‌ها در خانه‌ها بماند و سایهٔ آرامی برای زندگی باشد.
                            </p>
                            <p>
                                ما به‌جای دنبال کردن مدهای زودگذر، روی طراحی‌های ساده و ماندگار کار می‌کنیم؛ قطعه‌هایی که امروز زیبا هستند و ده سال بعد هم با خانهٔ شما هماهنگ‌اند.
                            </p>
                        </div>
                    </div>

                    <dl className='grid grid-cols-2 gap-4 self-center lg:col-span-6'>
                        {STATS.map((stat) => (
                            <div key={stat.label} className='flex flex-col gap-2 rounded-3xl border border-border bg-card p-6 sm:p-8'>
                                <dt className='text-sm leading-6 text-muted-foreground'>
                                    {stat.label}
                                </dt>

                                <dd className='order-first font-heading text-4xl font-semibold text-foreground sm:text-5xl'>
                                    {stat.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </Container>
            </section>

            <section aria-labelledby='values-title' className='bg-secondary/60 py-20 lg:py-28'>
                <Container>
                    <SectionHeading
                        align='center'
                        eyebrow='ارزش‌های ما'
                        title='آنچه هر روز در کارگاه به آن پایبندیم'
                        titleId='values-title'
                    />

                    <ul className='mt-14 grid gap-6 md:grid-cols-3'>
                        {VALUES.map((value) => (
                            <li key={value.title} className='rounded-3xl border border-border bg-card p-8'>
                                <span className='flex size-12 items-center justify-center rounded-2xl bg-accent text-brand'>
                                    <value.icon className='size-5' aria-hidden='true' />
                                </span>

                                <h3 className='mt-6 text-lg font-semibold text-foreground'>
                                    {value.title}
                                </h3>

                                <p className='mt-3 text-sm leading-7 text-muted-foreground'>
                                    {value.text}
                                </p>
                            </li>
                        ))}
                    </ul>
                </Container>
            </section>

            <section aria-labelledby='workshop-title' className='py-20 lg:py-28'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                    <div className='lg:col-span-4'>
                        <div className='lg:sticky lg:top-28'>
                            <SectionHeading
                                eyebrow='روند ساخت'
                                title='از چوب خام تا خانهٔ شما'
                                titleId='workshop-title'
                                description='ساخت هر قطعه از نارون پنج مرحله دارد و هر مرحله را استادکاری انجام می‌دهد که سال‌ها در همان کار تجربه دارد.'
                            />
                        </div>
                    </div>

                    <ol className='relative ms-6 flex flex-col gap-10 border-s border-border ps-8 sm:ms-0 lg:col-span-8 lg:ps-12'>
                        {WORKSHOP_STEPS.map((step) => (
                            <li key={step.title} className='relative'>
                                <span className='absolute -start-[3.35rem] top-0 flex size-11 items-center justify-center rounded-full border border-border bg-background text-brand lg:-start-[4.35rem]'>
                                    <step.icon className='size-5' aria-hidden='true' />
                                </span>

                                <h3 className='text-xl font-semibold text-foreground'>
                                    {step.title}
                                </h3>

                                <p className='mt-2 max-w-xl text-base leading-8 text-muted-foreground'>
                                    {step.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                </Container>
            </section>

            <ShowroomSection />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AboutPageProps> = async () => {
    return {
        props: { layout: await getLayoutData() },
        revalidate: 300
    }
}

export default AboutPage
